import { Injectable, OnDestroy } from '@angular/core';
import { CreateWorkListsRequestLocalStorage } from '@model/laboratory/worklist.model';
import { WorklistsStorageGateway } from '@service/laboratory/worklist/worklists-storage.gateway';

/**
 * Payload structure for sessionStorage
 */
interface SessionStoragePayload {
  data: CreateWorkListsRequestLocalStorage;
  timestamp: number;
  version: number;
}

/**
 * Type guard to validate sessionStorage payload
 */
function isValidPayload(value: unknown): value is SessionStoragePayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj['timestamp'] === 'number' &&
    typeof obj['version'] === 'number' &&
    obj['data'] !== undefined
  );
}

/**
 * Worklists Memory Storage Adapter
 *
 * Secure in-memory storage implementation for worklists data.
 * - Data is stored in memory only (not persistent across page reloads)
 * - Uses sessionStorage as fallback for tab persistence during navigation
 * - Automatically clears on service destruction
 * - Provides explicit clear method for logout scenarios
 *
 * Security considerations:
 * - Avoids localStorage to prevent XSS data exfiltration
 * - SessionStorage is tab-isolated and cleared on tab close
 * - Explicit TTL and cleanup mechanisms
 * - Data is base64 encoded for minimal protection
 */
@Injectable()
export class WorklistsMemoryStorageAdapter
  extends WorklistsStorageGateway
  implements OnDestroy
{
  private static readonly STORAGE_KEY = 'worklists_session';
  private static readonly TTL_MS = 30 * 60 * 1000; // 30 minutes TTL

  private memoryState: CreateWorkListsRequestLocalStorage | null = null;
  private lastUpdated = 0;

  constructor() {
    super();
    this.restoreFromSessionStorage();
    this.setupCleanupOnUnload();
  }

  ngOnDestroy(): void {
    this.clearAll();
  }

  /**
   * Store worklists data securely in memory
   */
  set(data: CreateWorkListsRequestLocalStorage): void {
    this.memoryState = data;
    this.lastUpdated = Date.now();
    this.syncToSessionStorage();
  }

  /**
   * Get worklists data from memory
   * Returns null if data is expired or not present
   */
  get(): CreateWorkListsRequestLocalStorage | null {
    if (this.isExpired()) {
      this.clearAll();
      return null;
    }
    return this.memoryState;
  }

  /**
   * Check if worklists data exists and is not expired
   */
  exists(): boolean {
    if (this.isExpired()) {
      this.clearAll();
      return false;
    }
    return this.memoryState !== null;
  }

  /**
   * Remove specific worklists by LG codes
   */
  deleteByLgList(lgList: string[]): void {
    if (!this.memoryState?.workLists) {
      return;
    }

    this.memoryState.workLists = this.memoryState.workLists.filter(
      (workList) => !lgList.includes(workList.lgCode)
    );

    this.lastUpdated = Date.now();
    this.syncToSessionStorage();
  }

  /**
   * Clear all worklists data from memory and session storage
   * Should be called on logout or when data is no longer needed
   */
  clearAll(): void {
    this.memoryState = null;
    this.lastUpdated = 0;
    this.clearSessionStorage();
  }

  /**
   * Check if stored data has expired based on TTL
   */
  private isExpired(): boolean {
    if (this.lastUpdated === 0) {
      return false;
    }
    return Date.now() - this.lastUpdated > WorklistsMemoryStorageAdapter.TTL_MS;
  }

  /**
   * Sync memory state to sessionStorage for tab persistence
   * Data is encoded to add minimal protection
   */
  private syncToSessionStorage(): void {
    try {
      if (this.memoryState) {
        const payload = {
          data: this.memoryState,
          timestamp: this.lastUpdated,
          version: 1
        };
        sessionStorage.setItem(
          WorklistsMemoryStorageAdapter.STORAGE_KEY,
          btoa(JSON.stringify(payload))
        );
      } else {
        this.clearSessionStorage();
      }
    } catch {
      // Silently fail - memory state is primary
    }
  }

  /**
   * Restore state from sessionStorage on service initialization
   * Only used for page refresh within same tab/session
   */
  private restoreFromSessionStorage(): void {
    try {
      const stored = sessionStorage.getItem(
        WorklistsMemoryStorageAdapter.STORAGE_KEY
      );
      if (!stored) {
        return;
      }

      const parsed: unknown = JSON.parse(atob(stored));

      if (!isValidPayload(parsed)) {
        this.clearSessionStorage();
        return;
      }

      // Validate TTL
      if (
        Date.now() - parsed.timestamp >
        WorklistsMemoryStorageAdapter.TTL_MS
      ) {
        this.clearSessionStorage();
        return;
      }

      this.memoryState = parsed.data;
      this.lastUpdated = parsed.timestamp ?? Date.now();
    } catch {
      // Corrupted data - clear it
      this.clearSessionStorage();
    }
  }

  /**
   * Clear session storage
   */
  private clearSessionStorage(): void {
    try {
      sessionStorage.removeItem(WorklistsMemoryStorageAdapter.STORAGE_KEY);
    } catch {
      // Silently fail
    }
  }

  /**
   * Setup cleanup on page unload to clear sensitive data
   */
  private setupCleanupOnUnload(): void {
    if (globalThis.window !== undefined) {
      globalThis.window.addEventListener('beforeunload', () => {
        // Note: sessionStorage persists across refresh but clears on tab close
        // This is the desired behavior for tab-isolated session storage
      });
    }
  }

  /**
   * Migrate data from old localStorage (if exists) and clear it
   * Call this once during app initialization
   */
  migrateFromLocalStorageAndClear(): void {
    try {
      const oldData = localStorage.getItem('worklists');
      if (oldData) {
        const parsed = JSON.parse(
          oldData
        ) as CreateWorkListsRequestLocalStorage;
        if (parsed && !this.memoryState) {
          this.memoryState = parsed;
          this.lastUpdated = Date.now();
          this.syncToSessionStorage();
        }
        // Always remove from localStorage for security
        localStorage.removeItem('worklists');
      }
    } catch {
      // Silently fail, ensure localStorage is cleared
      try {
        localStorage.removeItem('worklists');
      } catch {
        // Ignore
      }
    }
  }
}
